import { getTopLevelSelectedNodeIds, type TreeNode } from "../Project/LeftBar/treeMultiSelectUtils";

export type AppendedSidebarTreeMentionsResult = {
  text: string;
  hasAppendedTreeMentions: boolean;
};

type SidebarDocument = {
  id: string;
  title?: string | null;
};

type SidebarFolder = {
  id: string;
  name?: string | null;
};

/** Brackets/parentheses can break mention markup parsing. */
function sanitizeMentionLabel(label: string): string {
  return label.replace(/[\[\]()]/g, "").trim();
}

function mentionAlreadyPresent(text: string, mentionId: string): boolean {
  const escapedId = mentionId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`\\(${escapedId}\\)`).test(text);
}

function shouldAppendSidebarTreeMentions(topLevelNodeIds: string[], nodesById: Record<string, TreeNode | undefined>): boolean {
  if (topLevelNodeIds.length >= 2) return true;
  if (topLevelNodeIds.length === 1) {
    return nodesById[topLevelNodeIds[0]]?.type === "folder";
  }
  return false;
}

/**
 * When the user multi-selects project-tree documents/folders and asks chat a question,
 * append visible mention tags so history shows what they referenced and the agent gets targets.
 */
export function appendSidebarTreeMentions(
  baseText: string,
  selectedNodeIds: string[],
  nodesById: Record<string, TreeNode | undefined>,
  documents: SidebarDocument[],
  folders: SidebarFolder[],
): AppendedSidebarTreeMentionsResult {
  const topLevelNodeIds = getTopLevelSelectedNodeIds(selectedNodeIds, nodesById);
  if (!shouldAppendSidebarTreeMentions(topLevelNodeIds, nodesById)) {
    return { text: baseText, hasAppendedTreeMentions: false };
  }

  const mentions: string[] = [];
  const seenMentionIds = new Set<string>();

  for (const nodeId of topLevelNodeIds) {
    const node = nodesById[nodeId];
    if (!node) {
      const documentMentionId = `document::${nodeId}`;
      const fallbackDoc = documents.find((entry) => entry.id === nodeId);
      if (fallbackDoc && !seenMentionIds.has(documentMentionId) && !mentionAlreadyPresent(baseText, documentMentionId)) {
        seenMentionIds.add(documentMentionId);
        const rawTitle = fallbackDoc.title?.trim() || "Untitled";
        const safeLabel = sanitizeMentionLabel(rawTitle) || "Untitled";
        mentions.push(`#[${safeLabel}](${documentMentionId})`);
        continue;
      }

      const folderMentionId = `folder::${nodeId}`;
      const fallbackFolder = folders.find((entry) => entry.id === nodeId);
      if (fallbackFolder && !seenMentionIds.has(folderMentionId) && !mentionAlreadyPresent(baseText, folderMentionId)) {
        seenMentionIds.add(folderMentionId);
        const rawName = fallbackFolder.name?.trim() || "Untitled Folder";
        const safeLabel = sanitizeMentionLabel(rawName) || "Untitled Folder";
        mentions.push(`#[${safeLabel}](${folderMentionId})`);
      }
      continue;
    }

    if (node.type === "document") {
      const mentionId = `document::${nodeId}`;
      if (seenMentionIds.has(mentionId) || mentionAlreadyPresent(baseText, mentionId)) continue;

      const doc = documents.find((entry) => entry.id === nodeId);
      seenMentionIds.add(mentionId);
      const rawTitle = doc?.title?.trim() || node.name?.trim() || "Untitled";
      const safeLabel = sanitizeMentionLabel(rawTitle) || "Untitled";
      mentions.push(`#[${safeLabel}](${mentionId})`);
      continue;
    }

    if (node.type === "folder") {
      const mentionId = `folder::${nodeId}`;
      if (seenMentionIds.has(mentionId) || mentionAlreadyPresent(baseText, mentionId)) continue;

      const folder = folders.find((entry) => entry.id === nodeId);
      seenMentionIds.add(mentionId);
      const rawName = folder?.name?.trim() || node.name?.trim() || "Untitled Folder";
      const safeLabel = sanitizeMentionLabel(rawName) || "Untitled Folder";
      mentions.push(`#[${safeLabel}](${mentionId})`);
    }
  }

  if (mentions.length === 0) {
    return { text: baseText, hasAppendedTreeMentions: false };
  }

  const trimmedBase = baseText.trimEnd();
  const separator = trimmedBase.length > 0 ? "\n\n" : "";
  return {
    text: `${trimmedBase}${separator}${mentions.join(" ")}`,
    hasAppendedTreeMentions: true,
  };
}
